import {
    type CentraLanguageCode,
    type LanguageCodeSchema,
    validateLanguageCode
} from "@/data/supported-languages"
import {
    type CentraMarketCode,
    type MarketCodeSchema,
    validateMarketCode
} from "@/data/supported-markets"
import { type CentraPricelistCode } from "@/data/supported-pricelists"
import type Logger from "@/lib/logger/Logger"
import logger from "@/lib/logger/logger-init"
import { type SafeParseResult } from "valibot"
import { type CountryCode, type CountryCommonNamesAndCode } from "@/data/country-data"
import { type ShippingCountry } from "@/data/shipping-countries"
import countryToDefaultLanguageMap, {
    type CountryToDefaultLanguageMap
} from "./country-to-default-language"
import marketCodeToMarketIdMap from "./market-code-to-market-id"
import marketIdToMarketCodeMap, {
    type MarketIdToMarketCodeMap
} from "./market-id-to-market-code"
import pricelistIdToPricelistCodeMap, {
    type PricelistIdToPricelistCodeMap
} from "./pricelist-id-to-pricelist-code"

const mapMarketIdToMarketCodeCurried =
    (
        logger: Logger,
        marketIdToMarketCodeMap: MarketIdToMarketCodeMap,
        validateMarketCode: (code: string) => SafeParseResult<typeof MarketCodeSchema>
    ) =>
    (marketId: number): CentraMarketCode | null => {
        const isValidNumber = (num: number): boolean =>
            typeof num === "number" && !isNaN(num)

        if (!isValidNumber(marketId)) {
            logger.error("Invalid marketId input: must be a number.")
            return null
        }

        const marketCode = marketIdToMarketCodeMap.get(marketId)
        if (!marketCode) {
            logger.error(`No market code found for market ID: ${marketId}`)
            return null
        }

        const result = validateMarketCode(marketCode)
        return result.success ? result.output : null
    }

export const mapMarketIdToMarketCode = mapMarketIdToMarketCodeCurried(
    logger,
    marketIdToMarketCodeMap,
    validateMarketCode
)

const mapCountryToDefaultLanguageCurried =
    (
        logger: Logger,
        countryToDefaultLanguageMap: CountryToDefaultLanguageMap,
        validateLanguageCode: (code: string) => SafeParseResult<typeof LanguageCodeSchema>
    ) =>
    (country: string): CentraLanguageCode | null => {
        const isValidString = (str: any): str is string =>
            typeof str === "string" && str.trim() !== ""

        const formatCountryString = (country: string): string =>
            country.trim().toLowerCase()

        if (!isValidString(country)) {
            logger.error("Invalid country input")
            return null
        }

        const formattedCountry = formatCountryString(country)
        const languageCode = countryToDefaultLanguageMap.get(
            formattedCountry as ShippingCountry
        )

        if (!languageCode) {
            logger.error(`No default language found for country: ${formattedCountry}`)
            return null
        }

        const result = validateLanguageCode(languageCode)

        return result.success ? result.output : null
    }

export const mapCountryToDefaultLanguage = mapCountryToDefaultLanguageCurried(
    logger,
    countryToDefaultLanguageMap,
    validateLanguageCode
)

const mapCountryNameToCountryCodeCurried =
    (logger: Logger) =>
    (countriesData: CountryCommonNamesAndCode[], country: string): CountryCode | null => {
        const isValidInput = (input: string): boolean =>
            typeof input === "string" && input.trim() !== ""

        const normalizeString = (str: string): string => {
            return str
                .replace(/\[.*?\]/g, "")
                .replace(/-/g, " ")
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9 ]/g, "")
        }

        const isCommonNameMatch = (
            data: CountryCommonNamesAndCode,
            countryName: string
        ): boolean => {
            return data.commonNames
                .split(",")
                .map(name => normalizeString(name).trim())
                .includes(countryName)
        }

        if (!isValidInput(country)) {
            logger.error(`Invalid country input: '${country}'`)

            return null
        }

        const countryNormalized = normalizeString(country)

        for (const data of countriesData) {
            if (isCommonNameMatch(data, countryNormalized)) {
                logger.debug(`Country code found for ${country}: ${data.alpha2Code}`)

                return data.alpha2Code as CountryCode
            }
        }

        logger.warn(`No country code found for ${country}`)

        return null
    }

export const mapCountryNameToCountryCode = mapCountryNameToCountryCodeCurried(logger)

const mapPricelistCodeToPricelistIdCurried =
    (logger: Logger, pricelistIdToPricelistCodeMap: PricelistIdToPricelistCodeMap) =>
    (pricelistCode: CentraPricelistCode): number | null => {
        if (typeof pricelistCode !== "string" || pricelistCode.trim() === "") {
            logger.error("Invalid pricelistCode input: must be a non-empty string.")

            return null
        }

        const pricelistEntry = Array.from(pricelistIdToPricelistCodeMap.entries()).find(
            ([, code]) => code === pricelistCode.toLowerCase()
        )

        return pricelistEntry ? pricelistEntry[0] : null
    }

export const mapPricelistCodeToPricelistId = mapPricelistCodeToPricelistIdCurried(
    logger,
    pricelistIdToPricelistCodeMap
)

export const mapMarketCodeToMarketId = (marketCode: CentraMarketCode): number | null => {
    if (typeof marketCode !== "string" || marketCode.trim() === "") {
        logger.error("Invalid marketCode input: must be a non-empty string.")

        return null
    }

    const marketEntry = Array.from(marketCodeToMarketIdMap.entries()).find(
        ([code]) => code === marketCode
    )

    return marketEntry ? marketEntry[1] : null
}
